import { useQuery } from "@tanstack/react-query";
import React, { useEffect, useState } from "react";
import QUERY_KEYS from "../../constants/queryKeys";
import { SVG } from "../../constants/assetLists";
import { handleGetClientByLoggedInUserService } from "../../services/clients";
import { isSuperAdmin } from "../../utils";

export type ScreenTypes = "users" | "roles" | "groups";

type ID = string;

interface BaseNode {
  id: ID;
  name: string;
  icon: string;
  apiIcon?: string;
}

export interface IClient {
  _id: string;
  name: string;
  icon: string;
}

export interface BranchData extends BaseNode {
  clientId: ID;
  isClient: false;
  isBranch: true;
  isSubBranch: false;
  subBranches: SubBranchData[]; // allow nested branches; use [] if none
}

export interface SubBranchData extends BaseNode {
  clientId: ID;
  branchId: ID;
  isClient: false;
  isBranch: false;
  isSubBranch: true;
}

export interface ClientDataType extends BaseNode {
  isClient: true;
  isBranch: false;
  isSubBranch: false;
  branches: BranchData[]; // top-level client has branches
}

// Optional: a union to treat both uniformly
export type ClientTreeNode = ClientDataType | BranchData;

/**
 * Converts the currently-selected location node into the flat filter params
 * expected by all list/query APIs.
 *
 * Client   → { clientId }
 * Branch   → { clientId, branchId }
 * SubBranch → { clientId, branchId, subBranchId }
 */
export function getLocationFilterParams(
  selectedLocation: ClientDataType | BranchData | SubBranchData | null,
): { clientId?: string; branchId?: string; subBranchId?: string } {
  if (!selectedLocation) return {};
  if (selectedLocation.isClient) return { clientId: selectedLocation.id };
  if (selectedLocation.isBranch) {
    return {
      clientId: selectedLocation.clientId,
      branchId: selectedLocation.id,
    };
  }
  // isSubBranch
  const sub = selectedLocation as SubBranchData;
  return {
    clientId: sub.clientId,
    branchId: sub.branchId,
    subBranchId: sub.id,
  };
}

interface ClientsContextType {
  // General
  allClients: ClientDataType[];
  selectedClient: IClient | null;
  setSelectedClient: React.Dispatch<React.SetStateAction<IClient | null>>;
  selectedLocation: ClientDataType | BranchData | SubBranchData | null;
  setSelectedLocation: React.Dispatch<
    React.SetStateAction<ClientDataType | BranchData | SubBranchData | null>
  >;
  // Recently copied checklist location indicators
  recentlyCopiedLocationIds: string[];
  setRecentlyCopiedLocationIds: React.Dispatch<React.SetStateAction<string[]>>;
}

const ClientsContext = React.createContext<ClientsContextType | null>(null);

type Props = {
  children: React.ReactNode;
};

function ClientsContextProvider({ children }: Props) {
  // General
  const [allClients, setAllClients] = useState<ClientDataType[]>([]);
  const [selectedClient, setSelectedClient] = useState<IClient | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<
    ClientDataType | BranchData | SubBranchData | null
  >(null);
  const [recentlyCopiedLocationIds, setRecentlyCopiedLocationIds] = useState<
    string[]
  >([]);

  const { data: allClientsData } = useQuery({
    queryKey: [QUERY_KEYS.GET_USER_CLIENT],
    queryFn: () => handleGetClientByLoggedInUserService(),
  });

  useEffect(() => {
    if (
      allClientsData &&
      Array.isArray(allClientsData?.data) &&
      allClientsData?.data?.length > 0
    ) {
      const transformedData = allClientsData?.data?.map((client: any) => ({
        id: client?._id,
        name: client?.name,
        icon: SVG.clientIcon,
        apiIcon: client?.logo?.url || "",
        isClient: true,
        isBranch: false,
        isSubBranch: false,
        branches: client?.branches?.map((branch: any) => ({
          id: branch?._id,
          name: branch?.name,
          icon: SVG.filterSidebarRestaurantIcon,
          apiIcon: branch?.logo?.url || "",
          isClient: false,
          isBranch: true,
          isSubBranch: false,
          clientId: client?._id,
          subBranches: branch?.subBranches?.map((subBranch: any) => ({
            id: subBranch?._id,
            name: subBranch?.name,
            icon: SVG.taskIcon,
            apiIcon: subBranch?.logo?.url || "",
            clientId: client?._id,
            branchId: branch?._id,
            isClient: false,
            isBranch: false,
            isSubBranch: true,
          })),
        })),
      }));
      setAllClients(transformedData);
    }
  }, [allClientsData]);

  useEffect(() => {
    // Find a branch or sub-branch anywhere in the tree by id
    const findNodeInTree = (
      clients: ClientDataType[],
      nodeId: string,
    ): BranchData | SubBranchData | null => {
      for (const client of clients) {
        for (const branch of client.branches || []) {
          if (branch.id === nodeId) return branch;
          for (const subBranch of branch.subBranches || []) {
            if (subBranch.id === nodeId) return subBranch;
          }
        }
      }
      return null;
    };

    const applyLocation = (
      node: ClientDataType | BranchData | SubBranchData,
    ) => {
      setSelectedLocation(node);
      if (node.isClient) {
        setSelectedClient({
          _id: node.id,
          name: node.name,
          icon: node.apiIcon || "",
        });
      } else {
        // Both BranchData and SubBranchData carry clientId
        const parentClient = allClients.find(
          (c) => c.id === (node as BranchData | SubBranchData).clientId,
        );
        if (parentClient) {
          setSelectedClient({
            _id: parentClient.id,
            name: parentClient.name,
            icon: parentClient.apiIcon || "",
          });
        }
      }
    };

    if (!allClients?.length) return;

    // 1) defaultBranchId from user object (may point to a branch or sub-branch)
    let defaultNode: BranchData | SubBranchData | null = null;
    try {
      const userStr = localStorage.getItem("user");
      if (userStr) {
        const user = JSON.parse(userStr);
        const defaultBranchId =
          user?.defaultBranchId?._id ?? user?.defaultBranchId;
        if (defaultBranchId) {
          defaultNode = findNodeInTree(allClients, defaultBranchId);
        }
      }
    } catch {
      // ignore parse errors
    }

    if (defaultNode) {
      applyLocation(defaultNode);
      return;
    }

    // 2) Stored location from localStorage
    const storedLocation = localStorage.getItem("selectedLocation");
    if (storedLocation) {
      try {
        const savedNode = JSON.parse(storedLocation);
        let foundNode: ClientDataType | BranchData | SubBranchData | null =
          null;

        if (savedNode.isClient) {
          foundNode = allClients.find((c) => c.id === savedNode.id) || null;
        } else {
          foundNode = findNodeInTree(allClients, savedNode.id);
        }

        if (foundNode) {
          applyLocation(foundNode);
          return;
        }
      } catch {
        // ignore parse errors
      }
    }

    // 3) Non-super-admin / non-network-owner: first client's first branch
    if (!isSuperAdmin() && allClients[0]?.branches?.length > 0) {
      applyLocation(allClients[0].branches[0]);
      return;
    }

    // 4) Fallback: first client
    applyLocation(allClients[0]);
  }, [allClients]);

  // Sync selectedLocation → selectedClient & localStorage
  useEffect(() => {
    if (!selectedLocation) return;

    const nonClientNode = selectedLocation as BranchData | SubBranchData;

    // Save to localStorage — include branchId for sub-branches so we can re-locate on reload
    localStorage.setItem(
      "selectedLocation",
      JSON.stringify({
        id: selectedLocation.id,
        isClient: selectedLocation.isClient,
        isBranch: selectedLocation.isBranch,
        isSubBranch: selectedLocation.isSubBranch,
        clientId: selectedLocation.isClient
          ? selectedLocation.id
          : nonClientNode.clientId,
        ...(selectedLocation.isSubBranch && {
          branchId: (selectedLocation as SubBranchData).branchId,
        }),
      }),
    );

    // Update selectedClient with the parent client's data (using apiIcon)
    if (selectedLocation.isClient) {
      setSelectedClient({
        _id: selectedLocation.id,
        name: selectedLocation.name,
        icon: selectedLocation.apiIcon || "",
      });
    } else {
      // Both BranchData and SubBranchData carry clientId
      const parentClient = allClients.find(
        (c) => c.id === nonClientNode.clientId,
      );
      if (parentClient) {
        setSelectedClient({
          _id: parentClient.id,
          name: parentClient.name,
          icon: parentClient.apiIcon || "",
        });
      }
    }
  }, [selectedLocation, allClients]);

  // Sync selectedClient → selectedLocation (when client is changed from dropdown)
  useEffect(() => {
    if (!selectedClient || !allClients.length) return;

    // Both BranchData and SubBranchData carry clientId
    const currentClientId = selectedLocation?.isClient
      ? selectedLocation.id
      : (selectedLocation as BranchData | SubBranchData)?.clientId;

    // Only update if the selected client is different from the current location's client
    if (currentClientId !== selectedClient._id) {
      const newClient = allClients.find((c) => c.id === selectedClient._id);
      if (newClient) {
        setSelectedLocation(newClient);
      }
    }
  }, [selectedClient, allClients]);

  return (
    <ClientsContext.Provider
      value={{
        allClients,
        selectedClient,
        setSelectedClient,
        selectedLocation,
        setSelectedLocation,
        recentlyCopiedLocationIds,
        setRecentlyCopiedLocationIds,
      }}
    >
      {children}
    </ClientsContext.Provider>
  );
}

export default ClientsContextProvider;

export const useClientsContext = () => {
  const context = React.useContext(ClientsContext);
  if (context === null) {
    throw new Error(
      "useClientsContext must be used within an ClientsContextProvider",
    );
  }
  return context;
};
